<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h2 class="modal-title">
          {{ $t("message.address_form.edit_address_costa_rica") }}
        </h2>
        <button @click="$emit('close')" class="close-button">×</button>
      </div>

      <!-- Disabled Address Warning Banner -->
      <div v-if="formData.disabled === 1" class="disabled-warning">
        <svg
          class="warning-icon"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <div>
          <strong>{{
            $t("message.address_form.address_disabled_warning")
          }}</strong>
          <p class="text-sm mt-1">
            {{ $t("message.address_form.address_disabled_description") }}
          </p>
        </div>
      </div>

      <div class="modal-body">
        <div v-if="loading" class="loading-container">
          <baseMiniSpinner></baseMiniSpinner>
        </div>

        <div v-else-if="error" class="error-message">
          {{ error }}
        </div>

        <form v-else @submit.prevent="saveAddress" class="address-form">
          <!-- Name -->
          <div class="form-group">
            <label class="form-label"
              >{{ $t("message.address_form.name") }} *</label
            >
            <input
              :value="formData.name"
              type="text"
              class="form-input bordered bg-gray-100 cursor-not-allowed"
              readonly
            />
            <p class="text-xs text-gray-500 mt-1">
              {{ $t("message.address_form.name_locked_hint") }}
            </p>
          </div>

          <!-- Company -->
          <div class="form-group">
            <label class="form-label">{{
              $t("message.address_form.company")
            }}</label>
            <input
              v-model="formData.company"
              type="text"
              class="form-input bordered"
              :placeholder="$t('message.address_form.enter_company')"
            />
          </div>

          <!-- Country (Fixed for Costa Rica) -->
          <div class="form-group">
            <label class="form-label"
              >{{ $t("message.address_form.country") }} *</label
            >
            <input
              v-model="formData.country"
              type="text"
              class="form-input bordered"
              disabled
            />
          </div>

          <!-- Provincia -->
          <div class="form-group">
            <label class="form-label">Provincia *</label>
            <select
              v-model="formData.codProvincia"
              @change="onProvinciaChange"
              class="form-input bordered"
              :disabled="loadingProvincias"
              required
            >
              <option value="">
                {{ loadingProvincias ? "Loading..." : "Select a provincia" }}
              </option>
              <option
                v-for="provincia in provincias"
                :key="provincia.provinciaId"
                :value="provincia.codProvincia"
              >
                {{ provincia.nombreProvincia }}
              </option>
            </select>
          </div>

          <!-- Canton -->
          <div class="form-group">
            <label class="form-label">Cantón *</label>
            <select
              v-model="formData.codCanton"
              @change="onCantonChange"
              class="form-input bordered"
              :disabled="!formData.codProvincia || loadingCantones"
              required
            >
              <option value="">
                {{ loadingCantones ? "Loading..." : "Select a cantón" }}
              </option>
              <option
                v-for="canton in cantones"
                :key="canton.cantonId"
                :value="canton.codCanton"
              >
                {{ canton.nombreCanton }}
              </option>
            </select>
          </div>

          <!-- Distrito -->
          <div class="form-group">
            <label class="form-label">Distrito *</label>
            <select
              v-model="formData.codDistrito"
              @change="onDistritoChange"
              class="form-input bordered"
              :disabled="!formData.codCanton || loadingDistritos"
              required
            >
              <option value="">
                {{ loadingDistritos ? "Loading..." : "Select a distrito" }}
              </option>
              <option
                v-for="distrito in distritos"
                :key="distrito.distritoId"
                :value="distrito.codDistrito"
              >
                {{ distrito.nombreDistrito }}
              </option>
            </select>
          </div>

          <!-- Barrio -->
          <div class="form-group">
            <label class="form-label">Barrio</label>
            <select
              v-model="formData.codBarrio"
              @change="onBarrioChange"
              class="form-input bordered"
              :disabled="!formData.codDistrito || loadingBarrios"
            >
              <option value="">
                {{
                  loadingBarrios ? "Loading..." : "Select a barrio (optional)"
                }}
              </option>
              <option
                v-for="barrio in barrios"
                :key="barrio.barrioId"
                :value="barrio.codBarrio"
              >
                {{ barrio.nombreBarrio }}
              </option>
            </select>
          </div>

          <!-- Street 1 -->
          <div class="form-group">
            <label class="form-label"
              >{{ $t("message.address_form.street1") }} *</label
            >
            <input
              v-model="formData.street1"
              type="text"
              class="form-input bordered"
              :placeholder="$t('message.address_form.enter_street_address')"
              required
            />
          </div>

          <!-- Street 2 -->
          <div class="form-group">
            <label class="form-label">{{
              $t("message.address_form.street2")
            }}</label>
            <input
              v-model="formData.street2"
              type="text"
              class="form-input bordered"
              :placeholder="$t('message.address_form.enter_additional_info')"
            />
          </div>

          <!-- Street 3 -->
          <div class="form-group">
            <label class="form-label">{{
              $t("message.address_form.street3")
            }}</label>
            <input
              v-model="formData.street3"
              type="text"
              class="form-input bordered"
              :placeholder="$t('message.address_form.enter_additional_info')"
            />
          </div>

          <!-- Postal Code -->
          <div class="form-group">
            <label class="form-label"
              >{{ $t("message.address_form.postal_code") }} *</label
            >
            <input
              v-model="formData.postalCode"
              type="text"
              class="form-input bordered"
              :placeholder="$t('message.address_form.enter_postal_code')"
              required
            />
          </div>

          <!-- Residential -->
          <div class="form-group">
            <label class="form-label"
              >{{ $t("message.order.residential") }} *</label
            >
            <select
              v-model="formData.residential"
              class="form-input bordered"
              required
            >
              <option value="1">{{ $t("common.yes") }}</option>
              <option value="0">{{ $t("common.no") }}</option>
            </select>
          </div>

          <!-- Status - Prominent Section -->
          <div
            class="status-section"
            :class="{ 'status-disabled': !addressEnabled }"
          >
            <div class="status-header">
              <svg
                v-if="!addressEnabled"
                class="status-icon-warning"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <svg
                v-else
                class="status-icon-success"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 class="status-title">
                {{ $t("message.address_form.address_status") }}
              </h3>
            </div>
            <label class="status-checkbox-label">
              <input
                v-model="addressEnabled"
                type="checkbox"
                class="status-checkbox-input"
              />
              <span class="status-text">
                <strong>{{
                  addressEnabled
                    ? $t("message.customer.enabled")
                    : $t("message.customer.disabled")
                }}</strong>
                <span class="status-description">
                  {{
                    addressEnabled
                      ? $t("message.address_form.address_active_description")
                      : $t("message.address_form.address_inactive_description")
                  }}
                </span>
              </span>
            </label>
          </div>

          <!-- Action Buttons -->
          <div class="modal-actions">
            <button type="button" @click="$emit('close')" class="btn-cancel">
              {{ $t("common.cancel") }}
            </button>
            <button type="submit" class="btn-save" :disabled="saving">
              {{ saving ? $t("common.saving") : $t("common.save_changes") }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import {
  updateAddressToCustomer,
  getAddressPaises,
  getAddressProvincias,
  getAddressCantones,
  getAddressDistritos,
  getAddressBarrios
} from "@/api/store/Infoauto.js";

export default {
  name: "InfoautoEditAddressModalCostaRica",
  props: {
    customerId: {
      type: String,
      required: true
    },
    address: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      error: null,
      formData: {
        dir_id: "",
        name: "",
        company: "",
        street1: "",
        street2: "",
        street3: "",
        postalCode: "",
        country: "COSTA RICA",
        codPais: "CRI",
        codProvincia: "",
        provincia: "",
        codCanton: "",
        canton: "",
        codDistrito: "",
        distrito: "",
        codBarrio: "",
        barrio: "",
        residential: "1",
        disabled: 0
      },
      paises: [],
      provincias: [],
      cantones: [],
      distritos: [],
      barrios: [],
      loadingProvincias: false,
      loadingCantones: false,
      loadingDistritos: false,
      loadingBarrios: false
    };
  },
  computed: {
    addressEnabled: {
      get() {
        return this.formData.disabled === 0;
      },
      set(value) {
        this.formData.disabled = value ? 0 : 1;
      }
    }
  },
  mounted() {
    this.initializeForm();
    this.loadLocationData();
  },
  methods: {
    initializeForm() {
      // Map the address prop to formData
      this.formData = {
        dir_id: this.address.infoautoAddressId,
        name: this.address.name || "",
        company: this.address.company || "",
        street1: this.address.street1 || "",
        street2: this.address.street2 || "",
        street3: this.address.street3 || "",
        postalCode: this.address.postalCode || "",
        country: this.address.country || "COSTA RICA",
        codPais: this.address.codPais || "CRI",
        codProvincia: this.address.codProvincia || "",
        provincia: this.address.provincia || "",
        codCanton: this.address.codCanton || "",
        canton: this.address.canton || "",
        codDistrito: this.address.codDistrito || "",
        distrito: this.address.distrito || "",
        codBarrio: this.address.codBarrio || "",
        barrio: this.address.barrio || "",
        residential: String(this.address.residential || "1"),
        disabled: this.address.disabled || 0
      };
    },
    async loadLocationData() {
      try {
        // Load paises first to get country name from code
        const paisesResponse = await getAddressPaises();
        this.paises = Array.isArray(paisesResponse) ? paisesResponse : [];

        // Get country name from code
        const selectedPais = this.paises.find(
          p => p.codPais === this.formData.codPais
        );
        const paisName = selectedPais?.nombrePais || this.formData.country;

        // Load provinces using country name
        this.loadingProvincias = true;
        this.provincias = await getAddressProvincias(paisName);
        this.loadingProvincias = false;
        console.log(this.provincias);

        // Load cantones if provincia is selected
        if (this.formData.codProvincia) {
          this.loadingCantones = true;
          this.cantones = await getAddressCantones(
            this.formData.codProvincia,
            this.formData.codPais,
            this.formData.country
          );
          this.loadingCantones = false;
        }

        // Load distritos if canton is selected
        if (this.formData.codCanton) {
          this.loadingDistritos = true;
          this.distritos = await getAddressDistritos(
            this.formData.codProvincia,
            this.formData.codCanton,
            this.formData.codPais,
            this.formData.country
          );
          this.loadingDistritos = false;
        }

        // Load barrios if distrito is selected
        if (this.formData.codDistrito) {
          this.loadingBarrios = true;
          this.barrios = await getAddressBarrios(
            this.formData.codProvincia,
            this.formData.codCanton,
            this.formData.codDistrito,
            this.formData.codPais,
            this.formData.country
          );
          this.loadingBarrios = false;
        }
      } catch (error) {
        console.error("Error loading location data:", error);
        this.error = "Failed to load location data";
      }
    },
    async onProvinciaChange() {
      const selected = this.provincias.find(
        p => p.codProvincia === this.formData.codProvincia
      );
      this.formData.provincia = selected?.nombreProvincia || "";

      // Reset dependent fields
      this.formData.codCanton = "";
      this.formData.canton = "";
      this.formData.codDistrito = "";
      this.formData.distrito = "";
      this.formData.codBarrio = "";
      this.formData.barrio = "";
      this.cantones = [];
      this.distritos = [];
      this.barrios = [];

      if (this.formData.codProvincia) {
        this.loadingCantones = true;
        try {
          this.cantones = await getAddressCantones(
            this.formData.codProvincia,
            this.formData.codPais,
            this.formData.country
          );
        } catch (error) {
          console.error("Error loading cantones:", error);
        } finally {
          this.loadingCantones = false;
        }
      }
    },
    async onCantonChange() {
      const selected = this.cantones.find(
        c => c.codCanton === this.formData.codCanton
      );
      this.formData.canton = selected?.nombreCanton || "";

      // Reset dependent fields
      this.formData.codDistrito = "";
      this.formData.distrito = "";
      this.formData.codBarrio = "";
      this.formData.barrio = "";
      this.distritos = [];
      this.barrios = [];

      if (this.formData.codCanton) {
        this.loadingDistritos = true;
        try {
          this.distritos = await getAddressDistritos(
            this.formData.codProvincia,
            this.formData.codCanton,
            this.formData.codPais,
            this.formData.country
          );
        } catch (error) {
          console.error("Error loading distritos:", error);
        } finally {
          this.loadingDistritos = false;
        }
      }
    },
    async onDistritoChange() {
      const selected = this.distritos.find(
        d => d.codDistrito === this.formData.codDistrito
      );
      this.formData.distrito = selected?.nombreDistrito || "";

      // Reset dependent fields
      this.formData.codBarrio = "";
      this.formData.barrio = "";
      this.barrios = [];

      if (this.formData.codDistrito) {
        this.loadingBarrios = true;
        try {
          this.barrios = await getAddressBarrios(
            this.formData.codProvincia,
            this.formData.codCanton,
            this.formData.codDistrito,
            this.formData.codPais,
            this.formData.country
          );
        } catch (error) {
          console.error("Error loading barrios:", error);
        } finally {
          this.loadingBarrios = false;
        }
      }
    },
    onBarrioChange() {
      const selected = this.barrios.find(
        b => b.codBarrio === this.formData.codBarrio
      );
      this.formData.barrio = selected?.nombreBarrio || "";
    },
    async saveAddress() {
      this.saving = true;
      this.error = null;

      try {
        // Prepare data for API
        const addressData = {
          dir_id: this.formData.dir_id,
          name: this.formData.name,
          company: this.formData.company || "",
          street1: this.formData.street1,
          street2: this.formData.street2 || null,
          street3: this.formData.street3 || null,
          postal_code: this.formData.postalCode || " ",
          country: this.formData.country,
          cod_pais: this.formData.codPais,
          cod_provincia: this.formData.codProvincia,
          cod_canton: this.formData.codCanton,
          cod_distrito: this.formData.codDistrito,
          cod_barrio: this.formData.codBarrio || "0",
          provincia: this.formData.provincia,
          canton: this.formData.canton,
          distrito: this.formData.distrito,
          barrio: this.formData.barrio || " ",
          residential: parseInt(this.formData.residential),
          disabled: this.formData.disabled,
          tipo_3: 1 // Costa Rica addresses are always tipo_3 = 1
        };

        console.log("Updating Costa Rica address:", addressData);

        const response = await updateAddressToCustomer(
          this.customerId,
          addressData
        );

        console.log("Address updated successfully:", response);

        this.$emit("address-updated", response);
        this.$emit("close");
      } catch (error) {
        console.error("Error updating address:", error);
        this.error =
          error.response?.data?.message || "Failed to update address";
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped lang="postcss">
.modal-overlay {
  @apply fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50;
  padding: 1rem;
}

.modal-content {
  @apply bg-white rounded-lg shadow-xl w-full;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  @apply flex justify-between items-center p-6 border-b;
}

.modal-title {
  @apply text-xl font-bold text-gray-900;
}

.close-button {
  @apply text-gray-400 hover:text-gray-600 text-3xl font-bold leading-none;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
}

.modal-body {
  @apply p-6;
}

.loading-container {
  @apply flex justify-center items-center py-12;
}

.error-message {
  @apply bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded;
}

.address-form {
  @apply space-y-4;
}

.form-group {
  @apply flex flex-col;
}

.form-label {
  @apply text-sm font-medium text-gray-700 mb-1;
}

.form-input {
  @apply w-full px-3 py-2 rounded-md;
}

.disabled-warning {
  @apply bg-red-50 border-l-4 border-red-500 p-4 flex items-start gap-3;
}

.warning-icon {
  @apply w-6 h-6 text-red-500 flex-shrink-0 mt-1;
}

.disabled-warning strong {
  @apply text-red-800 text-lg;
}

.disabled-warning p {
  @apply text-red-700;
}

.status-section {
  @apply border-2 rounded-lg p-4 transition-all;
  border-color: #10b981;
  background: linear-gradient(to right, #f0fdf4, #dcfce7);
}

.status-section.status-disabled {
  border-color: #ef4444;
  background: linear-gradient(to right, #fef2f2, #fee2e2);
}

.status-header {
  @apply flex items-center gap-2 mb-3;
}

.status-title {
  @apply text-lg font-bold text-gray-800;
}

.status-icon-warning {
  @apply w-6 h-6 text-red-500;
}

.status-icon-success {
  @apply w-6 h-6 text-green-500;
}

.status-checkbox-label {
  @apply flex items-start gap-3 cursor-pointer;
}

.status-checkbox-input {
  @apply w-6 h-6 mt-1 cursor-pointer;
}

.status-text {
  @apply flex flex-col;
}

.status-text strong {
  @apply text-xl mb-1;
}

.status-text .status-description {
  @apply text-sm text-gray-600;
}

.modal-actions {
  @apply flex justify-end space-x-3 mt-6 pt-4 border-t;
}

.btn-cancel {
  @apply px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50;
}

.btn-save {
  @apply px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700;
}

.btn-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Encima de la cabecera fija del modal de orden (z-index 60), que tapaba el
   titulo y el boton de cerrar */
.modal-overlay {
  z-index: 70;
}

/* Movil: pantalla completa, con cabecera (y cerrar) y botones siempre a la
   vista; antes la X se iba con el scroll del formulario */
@media (max-width: 767px) {
  .modal-overlay {
    padding: 0;
  }

  .modal-content {
    height: 100%;
    max-height: none;
    border-radius: 0;
  }

  .modal-header {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 0.5rem 0.5rem 0.5rem 1rem;
    background: white;
  }

  .close-button {
    width: 44px;
    height: 44px;
  }

  .modal-body {
    padding: 1rem;
  }

  /* Selector largo: le gana al space-y del form (margen inferior) y el
     !important a la regla de <=390px del modal de orden, que lo dejaba al
     ancho del form en vez de llegar a los bordes */
  .modal-overlay .address-form > .modal-actions {
    position: sticky;
    bottom: 0;
    max-width: none !important;
    margin: 1.5rem -1rem -1rem;
    padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
    background: white;
  }

  .modal-actions button {
    flex: 1;
    min-height: 44px;
  }
}
</style>
